import React from "react";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import Reveal from "../anim/Reveal";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./Manifesto.module.css";

/*
 * Manifesto — the homepage's ONE <h1>. Olive, viewport-tall, centred (ref s02):
 * a small emblem + micro caption, the giant mixed sans / serif statement, one
 * supporting line. Nothing else — no buttons, no cards.
 *
 * Every word on screen comes from src/constants/siteContent.js — edit it THERE:
 *   emblem     first letter of SITE_CONTENT.brand
 *   caption    heroBadges[0] · locations.offices[0]
 *   statement  hero.title + everything AFTER the first sentence of hero.subtitle
 *   body line  the first sentence of hero.subtitle
 * The statement is assembled, not retyped, so it can never claim something the
 * hero copy does not. There is no founding year and no crest in SITE_CONTENT,
 * so neither appears here.
 *
 * Motion: the three blocks ride <Reveal> (reduced-motion safe in its own CSS).
 * HeadingWipe (App.js) only touches "main h2", so this h1 is left alone.
 */

/* TODO(content): which words of the statement set in the serif accent face.
   Lower-case, matched as whole words, punctuation ignored; keep it to 2-3. A
   word that is no longer in the hero copy is simply skipped. */
const ACCENT_WORDS = ["software", "transparent", "own"];

/* TODO(content): separator between the two caption facts. */
const CAPTION_SEPARATOR = " · ";

const TITLE_ID = "manifesto-title";

/* A short hyphenated compound ("Founder-led") is one idea, so it is never
   broken at its hyphen. Capped at 12 characters on purpose: that is ~240px at
   the 44px floor of --fs-statement, inside the 288px a 320px phone leaves, so
   a kept word can never push a line past the container. Longer compounds keep
   the browser's normal break-after-hyphen. */
const KEEP_TOGETHER_MAX = 12;

/* "First sentence. The rest." -> ["First sentence.", "The rest."]
   No regex lookbehind on purpose: it is a SYNTAX error in Safari < 16.4 and
   would take the whole bundle down with it. */
function splitFirstSentence(text) {
  const clean = String(text || "").trim();
  const match = /^(.+?[.!?])\s+(.+)$/.exec(clean);
  return match ? [match[1], match[2]] : [clean, ""];
}

function withFullStop(text) {
  const clean = String(text || "").trim();
  if (!clean) {
    return "";
  }
  return /[.!?]$/.test(clean) ? clean : `${clean}.`;
}

/* leading punctuation · the word · trailing punctuation — every part optional,
   so exec() always matches. */
const TOKEN = /^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/;

/* Wraps each accent word in <em> (Statement sets it in Instrument Serif,
   coloured var(--section-accent)). Punctuation stays OUTSIDE the <em>, in the
   sans, exactly like the comma after the serif word in the reference. */
function renderAccented(text) {
  const tokens = text.split(/\s+/).filter(Boolean);
  const nodes = [];

  tokens.forEach((token, index) => {
    const [, before, word, after] = TOKEN.exec(token);
    // The space lives OUTSIDE any wrapper, so a line can always break after it.
    const gap = index < tokens.length - 1 ? " " : "";
    const keep = token.indexOf("-") > 0 && token.length <= KEEP_TOGETHER_MAX;

    if (word && ACCENT_WORDS.includes(word.toLowerCase())) {
      nodes.push(
        before,
        <em key={index} className={keep ? styles.keep : undefined}>
          {word}
        </em>,
        `${after}${gap}`
      );
    } else if (keep) {
      nodes.push(
        <span key={index} className={styles.keep}>
          {token}
        </span>,
        gap
      );
    } else {
      nodes.push(`${token}${gap}`);
    }
  });

  return nodes;
}

const hero = SITE_CONTENT.hero || {};
const offices = (SITE_CONTENT.locations && SITE_CONTENT.locations.offices) || [];
const badges = SITE_CONTENT.heroBadges || [];

const [firstSentence, remainder] = splitFirstSentence(hero.subtitle);
const statementText = [withFullStop(hero.title), remainder].filter(Boolean).join(" ");
const caption = [badges[0], offices[0]].filter(Boolean).join(CAPTION_SEPARATOR);
const monogram = String(SITE_CONTENT.brand || "").trim().charAt(0);

function Manifesto() {
  if (!statementText) {
    return null;
  }

  return (
    <Section tone="olive" size="screen" id="manifesto" aria-labelledby={TITLE_ID}>
      <div className={styles.inner}>
        {monogram || caption ? (
          <Reveal className={styles.intro}>
            {monogram ? (
              <span className={styles.monogram} aria-hidden="true">
                {monogram}
              </span>
            ) : null}
            {caption ? (
              <MicroLabel as="p" className={styles.caption}>
                {caption}
              </MicroLabel>
            ) : null}
          </Reveal>
        ) : null}

        <Reveal delay={80}>
          <Statement
            as="h1"
            size="statement"
            align="center"
            id={TITLE_ID}
            className={styles.statement}
          >
            {renderAccented(statementText)}
          </Statement>
        </Reveal>

        {firstSentence ? (
          <Reveal as="p" delay={160} className={styles.lead}>
            {firstSentence}
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

export default Manifesto;
